import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import OutreachModal from "@/components/OutreachModal";
import { BellRing, CheckCircle2, Clock, MailCheck, CalendarX2, ShieldCheck, Filter, CheckCheck, BellOff, Loader2, Search, MessageSquareText } from "lucide-react";

function fmt(iso) {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const STATUS = { upcoming: "bg-blue-100 text-blue-700", overdue: "bg-red-100 text-red-700", completed: "bg-emerald-100 text-emerald-700" };

function ReminderCell({ s }) {
  if (!s || s.total === 0) return <span className="text-xs text-slate-400">No auto-reminders</span>;
  if (s.rebooked) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700" data-testid="reminder-rebooked">
        <ShieldCheck className="h-3.5 w-3.5" /> Rebooked — reminders stopped
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {s.items.map((it) => {
        let state = "pending";
        if (it.skipped) state = "skipped";
        else if (it.sent) state = "sent";
        const cls = { sent: "bg-emerald-100 text-emerald-700", skipped: "bg-slate-100 text-slate-500" }[state] || "bg-amber-100 text-amber-700";
        const Icon = { sent: MailCheck, skipped: CalendarX2 }[state] || Clock;
        const label = it.offset_days === 0 ? "due" : `+${it.offset_days}d`;
        return (
          <span key={it.offset_days} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`} title={`${label} · ${fmt(it.due_date)} · ${state}`} data-testid={`reminder-chip-${it.offset_days}`}>
            <Icon className="h-3 w-3" /> {label}
          </span>
        );
      })}
      <span className="ml-1 text-[10px] text-slate-400">{s.sent}/{s.total} sent</span>
    </div>
  );
}

export default function Recalls() {
  const [recalls, setRecalls] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");
  const [outreach, setOutreach] = useState(null);
  const [rebooked, setRebooked] = useState(0);

  const load = useCallback(() => api.get("/recalls/board").then((r) => { setRecalls(r.data.recalls || []); setRebooked(r.data.rebooked_count || 0); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const complete = async (r) => {
    setBusy(r.id + "-c");
    try {
      const { data } = await api.put(`/recalls/${r.id}`, { status: "completed" });
      toast.success(data.cliniko_synced ? "Recall completed · synced to Cliniko" : "Recall marked complete");
      load();
    } catch { toast.error("Could not update recall"); }
    finally { setBusy(null); }
  };

  const cancelReminders = async (r) => {
    if (!window.confirm("Stop the reminder emails and archive this recall in Cliniko?")) return;
    setBusy(r.id + "-x");
    try {
      const { data } = await api.post(`/recalls/${r.id}/cancel-reminders`);
      const bits = [];
      if (data.cancelled) bits.push(`${data.cancelled} reminder${data.cancelled > 1 ? "s" : ""} cancelled`);
      if (data.cliniko_archived) bits.push("Cliniko recall archived");
      toast.success(bits.length ? bits.join(" · ") : "No pending reminders");
      load();
    } catch { toast.error("Could not cancel reminders"); }
    finally { setBusy(null); }
  };

  const needle = q.trim().toLowerCase();
  const shown = recalls.filter((r) =>
    (filter === "all" || (r.status || "upcoming") === filter) &&
    (!needle || `${r.patient_name || ""} ${r.cliniko_id || ""} ${r.reason || ""}`.toLowerCase().includes(needle)));
  const withReminders = recalls.filter((r) => r.reminder_summary?.total > 0).length;

  return (
    <Layout title="Recalls" subtitle="Every scheduled recall and the status of its automated booking reminders">
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-indigo-50 px-4 py-2.5 text-sm text-indigo-800">
        <BellRing className="h-4 w-4" /> {recalls.length} recall{recalls.length === 1 ? "" : "s"} to chase · {withReminders} with automated email reminders. Reminders stop automatically once a patient rebooks.
        {rebooked > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700" data-testid="recalls-rebooked-count">
            <CheckCircle2 className="h-3.5 w-3.5" /> {rebooked} recently rebooked — cleared from the list
          </span>
        )}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="recalls-search" placeholder="Search by patient name, ID or reason"
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500" />
      </div>

      <div className="mb-4 flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-400" />
        {["all", "upcoming", "overdue", "completed"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`recalls-filter-${f}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-all ${filter === f ? "bg-teal-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Patient</th><th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Due</th><th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Reminder emails</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`recall-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-800">{r.patient_name}</div>
                  <div className="font-mono text-[11px] text-slate-400">{r.cliniko_id}</div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="line-clamp-2 max-w-xs cursor-help" title={r.reason} data-testid={`recall-reason-${r.id}`}>{r.reason}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-700">{fmt(r.due_date)}</div>
                  {r.days_overdue > 0 && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700" data-testid={`recall-overdue-${r.id}`}>
                      {r.days_overdue}d overdue
                    </span>
                  )}
                </td>
                <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS[r.status] || STATUS.upcoming}`}>{r.status || "upcoming"}</span></td>
                <td className="px-4 py-3"><ReminderCell s={r.reminder_summary} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOutreach({ name: r.patient_name, cliniko_id: r.cliniko_id, reason: r.reason, email: r.email, phone: r.phone })} data-testid={`recall-nudge-${r.id}`} className="border-teal-200 text-teal-700 hover:bg-teal-50">
                      <MessageSquareText className="mr-1 h-3.5 w-3.5" /> Nudge
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => complete(r)} disabled={busy === r.id + "-c" || r.status === "completed"} data-testid={`recall-complete-${r.id}`} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                      {busy === r.id + "-c" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCheck className="mr-1 h-3.5 w-3.5" /> Complete</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => cancelReminders(r)} disabled={busy === r.id + "-x" || !(r.reminder_summary?.pending > 0)} data-testid={`recall-cancel-reminders-${r.id}`} className="border-slate-200 text-slate-600 hover:bg-slate-50">
                      {busy === r.id + "-x" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><BellOff className="mr-1 h-3.5 w-3.5" /> Stop</>}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-sm text-slate-400">
                <CheckCircle2 className="mx-auto mb-2 h-7 w-7 text-slate-300" /> No recalls in this view. Set one from a plan.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {outreach && <OutreachModal open={!!outreach} onOpenChange={(o) => !o && setOutreach(null)} patient={outreach} />}
    </Layout>
  );
}
